/* Moda Warsaw — cart drawer shipment protection toggle.
   Load globally, unconditionally, from snippets/cart-drawer.liquid (same
   pattern as cart-drawer.js / lm-cart-upsell.js):
   <script type="module" src="{{ 'lm-cart-shipping-protection.js' | asset_url }}" fetchpriority="low"></script>
   Place this file in: assets/lm-cart-shipping-protection.js

   Recovered from the old, unpublished "Horizon - with shipping toggle" theme
   (gid://shopify/OnlineStoreTheme/196526375239), assets/eu-tax-toggle.js.
   The core mechanism (add/remove a real product variant on toggle,
   localStorage persistence, checkout interception, wrapping fetch/XHR to
   catch any cart add) is preserved faithfully. What changed for this
   theme's current drawer:

   - The old script refreshed the drawer by dispatching a bare
     `cart:refresh` CustomEvent on document.documentElement. This theme's
     cart-items-component (assets/component-cart-items.js) does not listen
     for that event at all — it listens for `@shopify/events`'
     StandardEvents.cartLinesUpdate, a class-based event carrying a resolved
     `.promise`, which a plain CustomEvent can't fake cheaply.
   - This store's own assets/lm-cart-upsell.js (a working, already-shipped
     cart-mutation component on this exact theme) solves the same problem by
     requesting the fresh `cart-drawer-section` markup in the same
     /cart/add.js or /cart/update.js call (`sections: 'cart-drawer-section'`)
     and swapping the returned <cart-items-component> innerHTML into the
     live one — then dispatching a lightweight `cart:update` CustomEvent
     (not `cart:refresh`) for any other component that wants to know the
     cart changed (e.g. lm-cart-cross-sell listens for exactly this). This
     file follows that same proven pattern instead of the old event name.
*/
(function () {
  'use strict';

  if (window.__lmShippingProtectionInit) return;
  window.__lmShippingProtectionInit = true;

  var VARIANT_ID = 54233826263367;
  var STORAGE_KEY = 'lm_shipping_protection_enabled_v1';
  var SECTION_ID = 'cart-drawer-section';

  var nativeFetch = window.fetch.bind(window);

  function wantsEnabled() {
    var saved = localStorage.getItem(STORAGE_KEY);
    return saved === null ? false : saved === '1';
  }

  function cartPost(endpoint, body) {
    return nativeFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart request failed: ' + res.status);
      return res.json();
    });
  }

  function getCart() {
    return nativeFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function isTaxInCart(cart) {
    return cart.items.some(function (i) { return i.variant_id === VARIANT_ID; });
  }

  function hasRealProduct(cart) {
    return cart.items.some(function (i) { return i.variant_id !== VARIANT_ID; });
  }

  function addTax() {
    return cartPost('/cart/add.js', {
      items: [{ id: VARIANT_ID, quantity: 1 }],
      sections: SECTION_ID
    });
  }

  function removeTax() {
    var updates = {};
    updates[VARIANT_ID] = 0;
    return cartPost('/cart/update.js', { updates: updates, sections: SECTION_ID });
  }

  /* Swap the returned cart-drawer-section markup into the live drawer, patch
     header cart-count bubbles, and signal other components. Mirrors
     assets/lm-cart-upsell.js's onClick handler exactly, since that pattern is
     already proven to work against this theme's drawer. */
  function applySectionResult(result) {
    var sectionHtml = result && result.sections && result.sections[SECTION_ID];

    if (sectionHtml) {
      var parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      var newComponent = parsed.querySelector('cart-items-component');
      var oldComponent = document.querySelector('cart-items-component');
      if (newComponent && oldComponent) {
        oldComponent.innerHTML = newComponent.innerHTML;
      }
    }

    return getCart().then(function (cart) {
      document.querySelectorAll('.cart-bubble, .theme-drawer__badge').forEach(function (el) {
        el.textContent = String(cart.item_count);
      });

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'lm-cart-shipping-protection',
            data: { itemCount: cart.item_count, source: 'lm-cart-shipping-protection' }
          }
        })
      );

      return cart;
    });
  }

  function paintToggle() {
    var btn = document.getElementById('lm-shipping-protection-btn');
    if (!btn) return;
    var on = wantsEnabled();
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Wyłącz ochronę przesyłki' : 'Włącz ochronę przesyłki');
  }

  var syncing = false;
  function syncCart() {
    if (syncing) return Promise.resolve();
    syncing = true;
    return getCart()
      .then(function (cart) {
        var inCart = isTaxInCart(cart);
        var want = wantsEnabled();
        var hasProduct = hasRealProduct(cart);

        /* Only add the protection product when there's a real product AND the toggle is on. */
        if (want && hasProduct && !inCart) {
          return addTax().then(applySectionResult);
        }
        /* Remove the protection product if the toggle is off, or the cart has no real products. */
        if (inCart && (!want || !hasProduct)) {
          return removeTax().then(applySectionResult);
        }
      })
      .then(paintToggle)
      .catch(function (e) {
        console.warn('[LM Shipping Protection] sync error', e);
      })
      .then(function () {
        syncing = false;
      });
  }

  /* Toggle click — delegated so it survives drawer re-renders. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('#lm-shipping-protection-btn');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;

    var newState = !wantsEnabled();
    localStorage.setItem(STORAGE_KEY, newState ? '1' : '0');
    btn.classList.add('is-loading');
    paintToggle();

    var op = newState ? addTax() : removeTax();
    op
      .then(applySectionResult)
      .catch(function (err) {
        console.warn('[LM Shipping Protection] toggle error', err);
        localStorage.setItem(STORAGE_KEY, newState ? '0' : '1');
        paintToggle();
      })
      .then(function () {
        btn.classList.remove('is-loading');
      });
  });

  /* Checkout interception — make sure the protection line is applied before navigating. */
  var proceeding = false;
  function ensureTaxThen(proceed) {
    if (!wantsEnabled()) {
      proceed();
      return;
    }
    getCart()
      .then(function (cart) {
        if (!isTaxInCart(cart)) return addTax().then(applySectionResult);
      })
      .catch(function (e) {
        console.warn('[LM Shipping Protection] checkout ensure error', e);
      })
      .then(proceed);
  }

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      var action = (form && form.getAttribute && form.getAttribute('action')) || '';
      var isCartForm = form && (form.id === 'cart-form' || action === '/cart' || action.indexOf('/checkout') !== -1);
      if (!isCartForm || proceeding || !wantsEnabled()) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      proceeding = true;
      ensureTaxThen(function () {
        window.location.href = '/checkout';
      });
    },
    true
  );

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('#checkout, [name="checkout"], .cart__checkout-button, a[href*="/checkout"]');
      if (!btn || proceeding || !wantsEnabled()) return;

      var form = btn.form || (btn.closest && btn.closest('form'));
      if (btn.type === 'submit' && form) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      proceeding = true;
      ensureTaxThen(function () {
        if (btn.tagName === 'A') window.location.href = btn.href;
        else if (form) form.submit();
        else window.location.href = '/checkout';
      });
    },
    true
  );

  /* Detect ANY product add by wrapping fetch, in case it didn't come through
     our own addTax()/removeTax() calls above (e.g. a quick-add elsewhere on
     the site, or this store's own lm-cart-upsell.js add). */
  window.fetch = function () {
    var args = arguments;
    var url = '';
    try {
      url = args[0] && args[0].url ? args[0].url : String(args[0] || '');
    } catch (e) {}
    var promise = nativeFetch.apply(null, args);
    if (url.indexOf('/cart/add') !== -1) {
      promise
        .then(function () {
          setTimeout(syncCart, 400);
        })
        .catch(function () {});
    }
    return promise;
  };

  /* Also wrap XMLHttpRequest in case anything on the site uses XHR for cart adds. */
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__lmSpUrl = url || '';
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr.__lmSpUrl && xhr.__lmSpUrl.indexOf('/cart/add') !== -1) {
      xhr.addEventListener('load', function () {
        setTimeout(syncCart, 400);
      });
    }
    return origSend.apply(this, arguments);
  };

  /* Also listen for this theme's own cart:update signal (dispatched by
     lm-cart-upsell.js and by ourselves) so the toggle repaints/re-syncs
     after any cart mutation, not just ones we caused. */
  document.addEventListener('cart:update', function () {
    paintToggle();
  });

  function boot() {
    paintToggle();
    syncCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
